import { useMemo, useState } from "react";
import { StepCampaignSelectProps } from "../CreateBriefTypes.ts";
import {
  RevEmptyState,
  RevInput,
  RevRadioGroup,
  type RevRadioOption,
  RevSpinner,
} from "@revmatics/catalyst";
import { useDebounce } from "../../../../../helpers/hooks/useDebounce.tsx";
import { faMagnifyingGlass } from "@fortawesome/pro-light-svg-icons";

export const StepCampaignSelect: React.FC<StepCampaignSelectProps> = ({
  campaigns,
  selectedCampaignId,
  onSelectCampaign,
  isLoading,
  error,
  control,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const filteredCampaignOptions: RevRadioOption[] = useMemo(() => {
    const allOptions: RevRadioOption[] = campaigns.map((campaign) => ({
      value: campaign.id.toString(),
      label: campaign.name,
    }));

    if (!debouncedSearchQuery) {
      return allOptions;
    }

    const lowerCaseSearch = debouncedSearchQuery.toLowerCase();
    return allOptions.filter(
      (option) =>
        option.label.toLowerCase().includes(lowerCaseSearch) ||
        option.subText?.toLowerCase().includes(lowerCaseSearch),
    );
  }, [campaigns, debouncedSearchQuery]);

  if (isLoading) {
    return <RevSpinner size="large" color="primary" centered />;
  }

  if (campaigns.length === 0) {
    return (
      <RevEmptyState
        title="No Campaigns Available"
        text="Please create campaigns first to proceed."
      />
    );
  }

  return (
    <div className="overflow-y-auto">
      <p className="typography-body-sm mb-2">Select Campaign</p>
      <RevInput
        name="campaignSearch"
        type="text"
        placeholder="Search"
        size="md"
        label=""
        iconLeft={faMagnifyingGlass}
        enableClearButton={true}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        onClear={() => setSearchQuery("")}
        className="mb-3 shadow-none"
      />
      <div className="p-2 border-monochrome-200 border rounded-lg max-h-[65dvh] lg:max-h-[150px] overflow-y-auto scroll-vertical">
        {filteredCampaignOptions.length === 0 && debouncedSearchQuery ? (
          <RevEmptyState
            title="No Matching Campaigns"
            text="Your search did not yield any results."
          />
        ) : filteredCampaignOptions.length === 0 && !debouncedSearchQuery ? (
          <RevEmptyState
            title="No Campaigns Available"
            text="Please create campaigns first to proceed."
          />
        ) : (
          <RevRadioGroup
            name="campaignId"
            options={filteredCampaignOptions}
            value={selectedCampaignId?.toString()}
            onChange={(val) => onSelectCampaign(Number(val))}
            displayDirection="vertical"
            error={error}
            isLabelBold
            control={control}
          />
        )}
      </div>
    </div>
  );
};
